export const SUPPORTED_LOCALES = ["en", "zh-CN"] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE = "en" satisfies SupportedLocale;

export type MessageValue = string | number | bigint | boolean | null | undefined;
export type MessageValues = Readonly<Record<string, MessageValue>>;
export type PluralCategory = Intl.LDMLPluralRule | `=${number}`;
export type PluralMessage = Readonly<
  Partial<Record<PluralCategory, string>> & {
    other: string;
  }
>;
export type TranslationMessage = string | PluralMessage;
export type MessageCatalog = Readonly<Record<string, TranslationMessage>>;

export interface I18n<Key extends string> {
  readonly locale: SupportedLocale;
  readonly t: (key: Key, values?: MessageValues) => string;
  readonly formatDate: (value: Date | number, options?: Intl.DateTimeFormatOptions) => string;
  readonly formatNumber: (value: number | bigint, options?: Intl.NumberFormatOptions) => string;
  readonly formatRelativeTime: (
    value: number,
    unit: Intl.RelativeTimeFormatUnit,
    options?: Intl.RelativeTimeFormatOptions,
  ) => string;
  readonly selectPlural: (value: number, options?: Intl.PluralRulesOptions) => Intl.LDMLPluralRule;
  readonly formatPlural: (value: number, message: PluralMessage, values?: MessageValues) => string;
}

export interface CreateI18nOptions<FallbackMessages extends MessageCatalog> {
  readonly locale: SupportedLocale;
  readonly fallbackMessages: FallbackMessages;
  readonly messages?: Readonly<
    Partial<
      Record<
        Exclude<SupportedLocale, typeof DEFAULT_LOCALE>,
        Readonly<Partial<Record<keyof FallbackMessages, TranslationMessage>>>
      >
    >
  >;
}

function canonicalizeLocale(locale: string): string | undefined {
  const candidate = locale.trim().replaceAll("_", "-");
  if (candidate.length === 0) {
    return undefined;
  }

  try {
    return Intl.getCanonicalLocales(candidate)[0];
  } catch {
    return undefined;
  }
}

export function matchSupportedLocale(
  locale: string | null | undefined,
): SupportedLocale | undefined {
  if (locale == null) {
    return undefined;
  }

  const canonical = canonicalizeLocale(locale);
  if (canonical == null) {
    return undefined;
  }

  if (canonical === "en" || canonical.startsWith("en-")) {
    return "en";
  }

  if (
    canonical === "zh" ||
    canonical === "zh-CN" ||
    canonical.startsWith("zh-CN-") ||
    canonical === "zh-SG" ||
    canonical.startsWith("zh-SG-") ||
    canonical === "zh-Hans" ||
    canonical.startsWith("zh-Hans-")
  ) {
    return "zh-CN";
  }

  return undefined;
}

export function resolveLocale(
  preferredLocales: string | ReadonlyArray<string> | null | undefined,
): SupportedLocale {
  const candidates =
    typeof preferredLocales === "string" ? [preferredLocales] : (preferredLocales ?? []);

  for (const candidate of candidates) {
    const locale = matchSupportedLocale(candidate);
    if (locale != null) {
      return locale;
    }
  }

  return DEFAULT_LOCALE;
}

function interpolate(
  message: string,
  values: MessageValues | undefined,
  numberFormat: Intl.NumberFormat,
): string {
  if (values == null) {
    return message;
  }

  return message.replace(/\{([A-Za-z][\w.-]*)\}/g, (placeholder, name: string) => {
    if (!Object.hasOwn(values, name)) {
      return placeholder;
    }

    const value = values[name];
    if (value == null) {
      return "";
    }
    if (typeof value === "number" || typeof value === "bigint") {
      return numberFormat.format(value);
    }
    return String(value);
  });
}

export function createI18n<const FallbackMessages extends MessageCatalog>(
  options: CreateI18nOptions<FallbackMessages>,
): I18n<Extract<keyof FallbackMessages, string>> {
  type Key = Extract<keyof FallbackMessages, string>;

  const { locale, fallbackMessages } = options;
  const localizedMessages = locale === DEFAULT_LOCALE ? undefined : options.messages?.[locale];
  const defaultNumberFormat = new Intl.NumberFormat(locale);
  const defaultPluralRules = new Intl.PluralRules(locale);
  const dateFormats = new Map<string, Intl.DateTimeFormat>();
  const numberFormats = new Map<string, Intl.NumberFormat>();
  const relativeTimeFormats = new Map<string, Intl.RelativeTimeFormat>();
  const pluralRules = new Map<string, Intl.PluralRules>();

  const optionsKey = (formatOptions: object | undefined): string =>
    formatOptions == null ? "" : JSON.stringify(formatOptions);

  const dateFormat = (dateOptions?: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
    const key = optionsKey(dateOptions);
    const cached = dateFormats.get(key);
    if (cached != null) {
      return cached;
    }
    const formatter = new Intl.DateTimeFormat(locale, dateOptions);
    dateFormats.set(key, formatter);
    return formatter;
  };

  const numberFormat = (numberOptions?: Intl.NumberFormatOptions): Intl.NumberFormat => {
    if (numberOptions == null) {
      return defaultNumberFormat;
    }
    const key = optionsKey(numberOptions);
    const cached = numberFormats.get(key);
    if (cached != null) {
      return cached;
    }
    const formatter = new Intl.NumberFormat(locale, numberOptions);
    numberFormats.set(key, formatter);
    return formatter;
  };

  const relativeTimeFormat = (
    relativeTimeOptions?: Intl.RelativeTimeFormatOptions,
  ): Intl.RelativeTimeFormat => {
    const key = optionsKey(relativeTimeOptions);
    const cached = relativeTimeFormats.get(key);
    if (cached != null) {
      return cached;
    }
    const formatter = new Intl.RelativeTimeFormat(locale, relativeTimeOptions);
    relativeTimeFormats.set(key, formatter);
    return formatter;
  };

  const pluralRule = (pluralOptions?: Intl.PluralRulesOptions): Intl.PluralRules => {
    if (pluralOptions == null) {
      return defaultPluralRules;
    }
    const key = optionsKey(pluralOptions);
    const cached = pluralRules.get(key);
    if (cached != null) {
      return cached;
    }
    const formatter = new Intl.PluralRules(locale, pluralOptions);
    pluralRules.set(key, formatter);
    return formatter;
  };

  const selectPlural = (
    value: number,
    pluralOptions?: Intl.PluralRulesOptions,
  ): Intl.LDMLPluralRule => pluralRule(pluralOptions).select(value);

  const formatPlural = (value: number, message: PluralMessage, values?: MessageValues): string => {
    const exactMessage = message[`=${value}`];
    const selectedMessage = exactMessage ?? message[selectPlural(value)] ?? message.other;
    return interpolate(selectedMessage, { ...values, count: value }, defaultNumberFormat);
  };

  const t = (key: Key, values?: MessageValues): string => {
    const message = localizedMessages?.[key] ?? fallbackMessages[key];
    if (message == null) {
      return key;
    }
    if (typeof message === "string") {
      return interpolate(message, values, defaultNumberFormat);
    }

    const count = values?.count;
    return typeof count === "number"
      ? formatPlural(count, message, values)
      : interpolate(message.other, values, defaultNumberFormat);
  };

  return {
    locale,
    t,
    formatDate: (value, dateOptions) => dateFormat(dateOptions).format(value),
    formatNumber: (value, numberOptions) => numberFormat(numberOptions).format(value),
    formatRelativeTime: (value, unit, relativeTimeOptions) =>
      relativeTimeFormat(relativeTimeOptions).format(value, unit),
    selectPlural,
    formatPlural,
  };
}
